import axios from "axios";
import { getDidYouMean, normalizeTerm } from "./searchUtils";
import { stripSearchStopwordsFromQuery } from "./searchStopwords";
import { containsRestrictedKeyword } from "./restrictedKeywords";

const VOCABULARY_CACHE_KEY = "shotkut-search-vocabulary-v1";
const VOCABULARY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const MIN_FUZZY_WORD_LENGTH = 4;

let memoryVocabulary = [];
let memoryUpdatedAt = null;
let inflightLoad = null;

const readLocalVocabulary = () => {
  try {
    const raw = localStorage.getItem(VOCABULARY_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.tags || !Array.isArray(parsed.tags)) return null;
    if (Date.now() - Number(parsed.savedAt || 0) > VOCABULARY_CACHE_TTL_MS) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const writeLocalVocabulary = (tags, updatedAt) => {
  try {
    localStorage.setItem(
      VOCABULARY_CACHE_KEY,
      JSON.stringify({
        tags,
        updatedAt,
        savedAt: Date.now(),
      }),
    );
  } catch (error) {
    // Ignore quota errors.
  }
};

export const loadSearchVocabulary = async ({ force = false } = {}) => {
  if (!force && memoryVocabulary.length > 0) {
    return {
      tags: memoryVocabulary,
      updatedAt: memoryUpdatedAt,
    };
  }

  if (!force) {
    const cached = readLocalVocabulary();
    if (cached?.tags?.length) {
      memoryVocabulary = cached.tags;
      memoryUpdatedAt = cached.updatedAt || null;
      return {
        tags: memoryVocabulary,
        updatedAt: memoryUpdatedAt,
      };
    }
  }

  if (inflightLoad) {
    return inflightLoad;
  }

  inflightLoad = axios
    .get(`${process.env.REACT_APP_BACKEND_URL}/search/vocabulary`)
    .then((response) => {
      const tags = Array.isArray(response.data?.tags)
        ? response.data.tags
        : [];
      memoryVocabulary = tags;
      memoryUpdatedAt = response.data?.updatedAt || null;
      if (tags.length > 0) {
        writeLocalVocabulary(tags, memoryUpdatedAt);
      }
      return {
        tags,
        updatedAt: memoryUpdatedAt,
      };
    })
    .catch(() => ({
      tags: memoryVocabulary,
      updatedAt: memoryUpdatedAt,
    }))
    .finally(() => {
      inflightLoad = null;
    });

  return inflightLoad;
};

export const getCachedSearchVocabulary = () => memoryVocabulary;

export const fuzzyCorrectWord = (word, vocabulary) => {
  const normalized = normalizeTerm(word);
  if (normalized.length < MIN_FUZZY_WORD_LENGTH) return word;
  if (!Array.isArray(vocabulary) || vocabulary.length === 0) return word;
  if (vocabulary.includes(normalized)) return word;

  const suggestion = getDidYouMean(normalized, vocabulary);
  return suggestion || word;
};

export const fuzzyCorrectSearchQuery = (query, vocabulary) => {
  const trimmed = String(query || "").trim();
  if (!trimmed) {
    return {
      original: "",
      corrected: "",
      changed: false,
      strippedStopwords: false,
    };
  }

  const { query: strippedQuery, tokens } = stripSearchStopwordsFromQuery(trimmed);
  const hadStopwords = strippedQuery.toLowerCase() !== trimmed.toLowerCase();

  if (!tokens.length) {
    return {
      original: trimmed,
      corrected: "",
      changed: false,
      strippedStopwords: hadStopwords,
      onlyStopwords: true,
    };
  }

  const corrected = tokens
    .map((word) => fuzzyCorrectWord(word, vocabulary))
    .join(" ");

  return {
    original: trimmed,
    corrected,
    changed: corrected.toLowerCase() !== trimmed.toLowerCase(),
    strippedStopwords: hadStopwords,
    onlyStopwords: false,
  };
};

export const prepareSearchTerm = async (query, options = {}) => {
  const { forceReload = false } = options;
  const { tags } = await loadSearchVocabulary({ force: forceReload });
  return fuzzyCorrectSearchQuery(query, tags);
};

/**
 * Pin slot #1 as the commit term for the dropdown:
 * - typo → local vocab fuzzy correction (Did you mean)
 * - exact → what the user typed
 * Then append DB/AI suggestions with dedupe.
 */
export const buildPinnedSuggestionList = async (
  typedQuery,
  apiSuggestions = [],
  maxItems = 5,
) => {
  const trimmed = String(typedQuery || "").trim();
  if (!trimmed) {
    return {
      suggestions: [],
      didYouMean: null,
      commitTerm: "",
      prepared: null,
    };
  }

  const prepared = await prepareSearchTerm(trimmed);
  if (prepared.onlyStopwords || !prepared.corrected) {
    return {
      suggestions: [],
      didYouMean: null,
      commitTerm: "",
      prepared,
    };
  }

  const pin = prepared.changed ? prepared.corrected : trimmed;
  const normalizedPin = pin.toLowerCase();
  const seen = new Set([normalizedPin]);
  const rest = [];

  (apiSuggestions || []).forEach((value) => {
    const suggestion = String(value || "").trim();
    if (!suggestion || containsRestrictedKeyword(suggestion)) return;
    const normalized = suggestion.toLowerCase();
    if (seen.has(normalized)) return;
    seen.add(normalized);
    rest.push(suggestion);
  });

  return {
    suggestions: [pin, ...rest].slice(0, maxItems),
    didYouMean: prepared.changed ? pin : null,
    commitTerm: pin,
    prepared,
  };
};
